import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import { queryClient } from './store/queryClient';
import LayoutWrapper from './components/Layout';
import routes from './routes';
import { getRawAuthState } from './api/utils/tokenRefreshContext';

/**
 * Authentication guard component that checks if user is authenticated.
 * Uses the tokenRefreshContext to check auth state (which is populated from localStorage on startup by main.tsx).
 * Also checks localStorage directly to handle cases where the in-memory state hasn't been initialized yet.
 */
const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const location = useLocation();
  
  // Check auth state from tokenRefreshContext (which was initialized from localStorage in main.tsx)
  // We use getRawAuthState to check for any token (even expired ones)
  const authState = getRawAuthState();
  const isAuthenticated = !!authState?.accessToken;
  
  // Also check localStorage directly as a fallback
  // This handles cases where the in-memory state hasn't been initialized yet
  const isAuthenticatedFromStorage = () => {
    if (typeof window === 'undefined') return false;
    const raw = localStorage.getItem('auth');
    if (!raw) return false;
    try {
      const parsed = JSON.parse(raw) as {
        accessToken: string;
        refreshToken: string;
        expiresAt: string;
        user: any;
      };
      const expiresAt = new Date(parsed.expiresAt);
      // Check if token is still valid (with a 5-minute buffer for clock skew)
      return expiresAt.getTime() > Date.now() - 300000;
    } catch {
      return false;
    }
  };
  
  const isAuth = isAuthenticated || isAuthenticatedFromStorage();
  
  return isAuth ? (
    children
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
};

const App: React.FC = () => {
  return (
    <React.StrictMode>
      <ConfigProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
            <Routes>
              {/* Root path redirect - handled outside of auth check */}
              <Route path="/" element={<Navigate to="/map" replace />} />
              
              {/* Public routes */}
              {routes.public.map((route) => (
                <Route key={route.path} path={route.path} element={route.element} />
              ))}
              
              {/* Protected routes wrapped with layout */}
              <Route element={<RequireAuth><LayoutWrapper /></RequireAuth>}>
                {routes.protected.map((route) => (
                  <Route key={route.path} path={route.path} element={route.element} />
                ))}
              </Route>
              
              {/* Fallback - redirect to map for any unknown routes */}
              <Route path="*" element={<Navigate to="/map" replace />} />
            </Routes>
          </Router>
        </QueryClientProvider>
      </ConfigProvider>
    </React.StrictMode>
  );
};

export default App;